import { getNodeCG } from "@livcg/nodecg-react";
import { OPLOG_MESSAGE, type OpLogEntry } from "@livcg/schema";
import { useCallback, useEffect, useState } from "react";
import { api } from "./api.js";

type Listener = (entry: OpLogEntry) => void;
const listeners = new Set<Listener>();
let wired = false;

/** unlisten is not typed in the NodeCG client — a single global listener,
 *  subscribers via a Set. */
function wire() {
  if (wired) return;
  const nodecg = getNodeCG();
  if (!nodecg) return;
  wired = true;
  nodecg.listenFor(OPLOG_MESSAGE, (data) => {
    for (const l of listeners) l(data as OpLogEntry);
  });
}

export function subscribeOpLog(listener: Listener): () => void {
  wire();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export type OpLogFilter = {
  slug: string | null;
  kind: "all" | "command" | "graphics" | "problems";
};

function matches(e: OpLogEntry, f: OpLogFilter): boolean {
  if (f.slug && e.slug !== null && e.slug !== f.slug) return false;
  if (f.kind === "problems") return e.level !== "info";
  if (f.kind === "command") return e.kind === "command";
  if (f.kind === "graphics") return e.kind === "graphics";
  return true;
}

/** A command entry gets its id after the ack (i.e. after the on-air entry), but
 *  ts is the moment of the press. For display we order by time. */
const newestFirst = (a: OpLogEntry, b: OpLogEntry) => b.ts - a.ts || b.id - a.id;

function query(f: OpLogFilter, before?: number): string {
  const p = new URLSearchParams({ limit: "200" });
  if (f.slug) p.set("slug", f.slug);
  if (f.kind === "problems") p.set("level", "warn,error");
  if (f.kind === "command") p.set("kind", "command");
  if (f.kind === "graphics") p.set("kind", "graphics");
  if (before) p.set("before", String(before));
  return `/api/oplog?${p.toString()}`;
}

/** History (REST) + live entries (NodeCG message), newest to oldest. */
export function useOpLog(filter: OpLogFilter) {
  const [entries, setEntries] = useState<OpLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const { slug, kind } = filter;

  useEffect(() => {
    let cancelled = false;
    const f = { slug, kind };
    setLoading(true);
    setError(null);
    const buffered: OpLogEntry[] = [];
    let loaded = false;
    const unsubscribe = subscribeOpLog((e) => {
      if (!matches(e, f)) return;
      if (!loaded) buffered.push(e);
      else
        setEntries((prev) =>
          prev.some((p) => p.id === e.id) ? prev : [e, ...prev].sort(newestFirst),
        );
    });
    api
      .get<OpLogEntry[]>(query(f))
      .then((rows) => {
        if (cancelled) return;
        loaded = true;
        const seen = new Set(rows.map((r) => r.id));
        const fresh = buffered.filter((e) => !seen.has(e.id));
        setEntries([...fresh, ...rows].sort(newestFirst));
        setHasMore(rows.length >= 200);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [slug, kind]);

  const loadMore = useCallback(() => {
    if (entries.length === 0) return;
    const minId = Math.min(...entries.map((e) => e.id));
    void api.get<OpLogEntry[]>(query({ slug, kind }, minId)).then((rows) => {
      setEntries((prev) => [...prev, ...rows]);
      setHasMore(rows.length >= 200);
    });
  }, [entries, slug, kind]);

  return { entries, loading, error, hasMore, loadMore };
}

/** Number of problems that arrived while the log was not open (topbar badge). */
export function useUnseenProblems(slug: string | null, open: boolean): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (open) {
      setCount(0);
      return;
    }
    return subscribeOpLog((e) => {
      if (e.level === "info") return;
      if (slug && e.slug !== null && e.slug !== slug) return;
      setCount((c) => c + 1);
    });
  }, [slug, open]);
  return count;
}

type SocketLike = { on(event: string, cb: (...args: unknown[]) => void): void };

let reporting = false;
let currentSlug: string | null = null;

export function setReportingSlug(slug: string | null) {
  currentSlug = slug;
}

function report(type: "error" | "reconnect", message: string) {
  void api
    .post("/api/oplog/client", { type, message: message.slice(0, 2000), slug: currentSlug })
    .catch(() => {
      /* no server — the next reconnect entry covers it */
    });
}

/** Browser-side technical problems: JS errors and loss of connection to the
 *  server. A command sent during an outage does not reach the server, so
 *  the outage interval is recorded on reconnect. */
export function startClientReporting() {
  if (reporting || typeof window === "undefined") return;
  reporting = true;

  window.addEventListener("error", (e) => {
    report("error", `${e.message} @ ${e.filename}:${e.lineno}`);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason as { message?: string } | undefined;
    report("error", `unhandledrejection: ${r?.message ?? String(e.reason)}`);
  });

  const socket = (window as { socket?: SocketLike }).socket;
  if (!socket) return;
  let lostAt: number | null = null;
  let reason = "";
  socket.on("disconnect", (r) => {
    lostAt = Date.now();
    reason = String(r ?? "");
  });
  socket.on("connect", () => {
    if (lostAt === null) return;
    const secs = ((Date.now() - lostAt) / 1000).toFixed(1);
    lostAt = null;
    report("reconnect", `Server bilan aloqa ${secs} s uzildi (${reason})`);
  });
}
